import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, ShieldAlert, Sparkles, CheckCircle2, Trophy } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function MyWarnings() {
  const [data, setData] = useState({ active: [], appreciations: [], summary: null });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: live }, me] = await Promise.all([
          api.get("/me/warnings"),
          api.get("/auth/me"),
        ]);
        setData(live);
        // Full history: get user_id from /auth/me then fetch warnings list
        const uid = me.data.id;
        const wAll = await api.get(`/users/${uid}/warnings`);
        const aAll = await api.get(`/users/${uid}/appreciations`);
        setHistory({ warnings: wAll.data, appreciations: aAll.data });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const s = data.summary || {};

  // Compute expired warnings once per change
  const expiredWarnings = useMemo(
    () => (history.warnings || []).filter((w) => w.expired_at),
    [history.warnings]
  );

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Conduct</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">
          My Warnings &amp; Appreciations
        </h1>
        <p className="text-sm text-zinc-500 mt-2">Track your conduct record and how close you are to clearing all warnings.</p>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : (
        <>
          {s?.appreciation_streak_earned && (
            <div className="flex items-center gap-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-md p-4" data-testid="appreciation-streak-trophy">
              <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700">Appreciation Streak</div>
                <div className="font-display text-xl font-bold tracking-tight">You've earned {s.appreciations_this_quarter} appreciations this quarter</div>
                <div className="text-sm text-amber-800/80 mt-0.5">Outstanding work — keep showing up like this.</div>
              </div>
            </div>
          )}
          {!s?.appreciation_streak_earned && (s?.appreciations_this_quarter ?? 0) > 0 && (
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500" data-testid="appreciation-streak-progress">
              <Trophy className="w-3.5 h-3.5 text-zinc-400" />
              {s.appreciations_this_quarter} / {s.appreciation_streak_threshold} appreciations this quarter — earn {s.appreciation_streak_threshold - s.appreciations_this_quarter} more to unlock the trophy.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Hard warnings" value={s.hard_count ?? 0} accent="red" testid="stat-hard" />
            <Stat label="Soft warnings" value={s.soft_count ?? 0} accent="orange" testid="stat-soft" />
            <Stat label="Strikes remaining" value={s.hard_strikes_remaining ?? 3} accent="zinc" testid="stat-strikes" />
            <Stat label="Appreciations to clear" value={s.appreciations_needed_to_clear ?? 3} accent="green" testid="stat-apprs" />
          </div>

          <Section title="Active warnings" testid="section-active">
            {(data.active || []).length === 0 ? (
              <Empty label="No active warnings — clean slate." icon={CheckCircle2} />
            ) : (
              <div className="space-y-3">
                {data.active.map((w) => (
                  <WarningRow key={w.id} w={w} />
                ))}
              </div>
            )}
          </Section>

          <Section title="Pending appreciations" testid="section-apprs">
            {(data.appreciations || []).length === 0 ? (
              <Empty label="No appreciations yet." icon={Sparkles} />
            ) : (
              <div className="space-y-3">
                {data.appreciations.map((a) => (
                  <AppreciationRow key={a.id} a={a} />
                ))}
              </div>
            )}
          </Section>

          {expiredWarnings.length > 0 && (
            <Section title="Past (expired) warnings" testid="section-history">
              <div className="space-y-2">
                {expiredWarnings.map((w) => (
                  <div key={w.id} className="border border-zinc-200 rounded-md p-3 opacity-70" data-testid={`expired-warning-${w.id}`}>
                    <div className="flex items-center justify-between text-[11px] text-zinc-500">
                      <span className="uppercase tracking-widest font-bold">{w.severity} · expired</span>
                      <span>{fmtDate(w.expired_at)}</span>
                    </div>
                    <div className="text-sm text-zinc-700 mt-1">{w.reason}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">{w.expired_reason || "Cleared"}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function WarningRow({ w }) {
  const isHard = w.severity === "hard";
  const Icon = isHard ? ShieldAlert : AlertTriangle;
  return (
    <div
      className={`border-l-4 rounded-md p-3 ${isHard ? "border-red-500 bg-red-50" : "border-orange-500 bg-orange-50"}`}
      data-testid={`active-warning-${w.id}`}
    >
      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${isHard ? "text-red-700" : "text-orange-700"}`}>
          <Icon className="w-3.5 h-3.5" /> {isHard ? "Hard Warning" : "Soft Warning"}
        </span>
        <span className="text-[11px] text-zinc-500">{fmtDate(w.created_at)}</span>
      </div>
      <div className="mt-1 text-sm font-medium text-zinc-900">{w.reason}</div>
      <div className="mt-1 text-[11px] text-zinc-500">
        Issued by {w.issued_by_name} · {w.acknowledged_at ? "Acknowledged" : "Pending acknowledgement"}
      </div>
    </div>
  );
}

function AppreciationRow({ a }) {
  return (
    <div className="border-l-4 border-emerald-500 bg-emerald-50 rounded-md p-3" data-testid={`appreciation-${a.id}`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700">
          <Sparkles className="w-3.5 h-3.5" /> Appreciation
        </span>
        <span className="text-[11px] text-zinc-500">{fmtDate(a.created_at)}</span>
      </div>
      <div className="mt-1 text-sm font-medium text-zinc-900">{a.reason}</div>
      <div className="mt-1 text-[11px] text-zinc-500">From {a.issued_by_name}</div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const palette = {
    red: "text-red-700",
    orange: "text-orange-700",
    green: "text-emerald-700",
    zinc: "text-zinc-900",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid={testid}>
      <div className="label-overline">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tight mt-1 ${palette[accent] || ""}`}>{value}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-bold tracking-tight mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ label, icon: Icon }) {
  return (
    <div className="py-6 text-center text-zinc-400 text-sm">
      <Icon className="w-6 h-6 mx-auto text-zinc-300 mb-2" />
      {label}
    </div>
  );
}
